// @generated by `pnpm content:generate` from src/content-export — DO NOT EDIT
import type { TravelerPack } from '../types.js'

export const CH: TravelerPack = {
  jurisdiction: {
    id: 'CH',
    type: 'country',
    name: 'Switzerland',
  },
  metros: [],
  quickFacts: {
    driveSide: 'right',
    motorwayLimitKmh: 120,
    alcoholLimit: '0.05% (0.01 novice/pro)',
    vignette: 'Required (annual only)',
    emergency: '112',
  },
  rules: [
    {
      id: 'ch-vignette',
      target: 'CH',
      category: 'vignette_toll',
      severity: 'high',
      title: 'Motorway vignette required',
      detail: 'You must display a vignette to use Swiss motorways. Only an annual sticker is sold — no daily/weekly option.',
      cost: 'CHF 40',
      citation: 'https://www.bazg.admin.ch/bazg/en/home/information-individuals/importation-vehicles-vignette/motorway-charge-sticker--vignette-.html',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'ch-speed-income',
      target: 'CH',
      category: 'limit',
      severity: 'high',
      title: 'Income-scaled speeding fines',
      detail: 'Serious speeding fines are scaled to income and can be very large; cameras are common.',
      cost: 'income-scaled',
      citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-licences-and-traffic-regulations/traffic-regulations/',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'ch-alcohol',
      target: 'CH',
      category: 'limit',
      severity: 'medium',
      title: '0.05% blood-alcohol (0.01 novice)',
      detail: 'General limit 0.05%; 0.01% for novice and professional drivers.',
      citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-licences-and-traffic-regulations/traffic-regulations/',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'ch-idp',
      target: 'CH',
      category: 'document',
      severity: 'medium',
      title: 'Carry licence + ID; IDP recommended',
      detail: 'Non-EU visitors should carry an International Driving Permit with their national licence.',
      citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-in-switzerland-with-a-foreign-driving-licence/',
      lastReviewed: '2026-06-02',
    },
  ],
  checklists: {
    pre: [
      {
        id: 'ch-pre-vignette',
        label: 'Plan for the motorway vignette',
        detail: 'Switzerland sells only an annual vignette — confirm your rental already has a valid one, or budget to buy it at the border.',
        citation: 'https://www.bazg.admin.ch/bazg/en/home/information-individuals/importation-vehicles-vignette/motorway-charge-sticker--vignette-.html',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'ch-pre-idp',
        label: 'Apply for your International Driving Permit',
        detail: 'Non-EU drivers should carry an IDP with the national licence.',
        citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-in-switzerland-with-a-foreign-driving-licence/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'ch-pre-insurance',
        label: 'Confirm rental insurance + winter readiness',
        detail: 'Check coverage and, in season, that the car has winter tyres and snow chains for Alpine passes.',
      },
      {
        id: 'ch-pre-offline',
        label: 'Download the offline maps + passes pack',
        detail: 'Mountain valleys lose signal; save offline maps and note which passes may be closed in winter.',
      },
    ],
    on: [
      {
        id: 'ch-on-vignette',
        label: 'Confirm the vignette is displayed before the motorway',
        detail: 'Driving the motorway without a valid vignette is fined on top of the sticker price.',
        citation: 'https://www.bazg.admin.ch/bazg/en/home/information-individuals/importation-vehicles-vignette/motorway-charge-sticker--vignette-.html',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'ch-on-speed',
        label: 'Respect limits exactly — fines scale with income',
        detail: 'Swiss speeding penalties for serious excess are income-scaled and severe; cameras are frequent.',
        citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-licences-and-traffic-regulations/traffic-regulations/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'ch-on-docs',
        label: 'Keep licence, IDP and ID reachable',
        detail: 'Carry the original documents for roadside checks.',
      },
    ],
    post: [
      {
        id: 'ch-post-portal',
        label: 'Watch for forwarded fine notices',
        detail: 'Camera fines may be forwarded via the rental company; check any official notice carefully.',
        citation: 'https://www.ch.ch/en/vehicles-and-traffic/driving-licences-and-traffic-regulations/traffic-regulations/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'ch-post-pay',
        label: 'Pay promptly to avoid escalation',
        detail: 'Settle any valid fine quickly; unpaid penalties can escalate and follow you internationally.',
      },
      {
        id: 'ch-post-dispute',
        label: 'Know your dispute rights',
        detail: 'You can contest a penalty; keep rental dates and photos as evidence.',
      },
    ],
  },
  course: {
    title: 'Driving Abroad Confidently',
    modules: [
      {
        id: 'ch-m1-before',
        title: 'Before You Go',
        minutes: 5,
        body: 'Switzerland rewards preparation. The headline item is the motorway vignette: it is sold only as an annual sticker, so confirm your rental already carries a valid one or plan to buy it at the border before you join any motorway. Non-EU drivers should carry an International Driving Permit with the national licence. If you travel in the colder months, check that the car has winter tyres and chains, because Alpine routes demand them. Download offline maps — mountain valleys drop signal fast — and note which high passes may be closed seasonally so your route does not dead-end at a barrier.',
        keyPoints: [
          'Vignette is annual-only — confirm or buy before the motorway',
          'Carry an IDP with your national licence',
          'Plan for winter tyres, chains and possible pass closures',
        ],
      },
      {
        id: 'ch-m2-car',
        title: 'The Car & The Side',
        minutes: 5,
        body: 'Switzerland drives on the right, with motorway limits typically up to 120 km/h. Roads are immaculate but mountainous: expect tunnels, steep gradients and tight switchbacks. On narrow Alpine roads the vehicle going uphill generally has priority, and postal buses always do — give way to them. Use a low gear on long descents to save your brakes rather than riding them. Spend a few minutes finding the lights, wipers and hazard switch before you set off. If you are not used to mountain driving, allow extra time; rushing a pass is where confident flatland drivers get caught out.',
        keyPoints: [
          'Drive on the right; motorway limit around 120 km/h',
          'Uphill traffic and postal buses have priority on narrow roads',
          'Use low gears on long descents',
        ],
      },
      {
        id: 'ch-m3-surprise',
        title: 'Rules That Surprise You',
        minutes: 5,
        body: 'Two things surprise visitors most. First, the vignette: there is no daily or weekly option, so even a short hop on a Swiss motorway needs the full annual sticker. Second, the cost of speeding. Serious excess is treated severely and fines for high-end offences are scaled to income, so the penalty for the same speed can dwarf what you would expect elsewhere. Cameras are common and well hidden. Alcohol limits are strict — effectively zero for novice and professional drivers. Treat posted limits as exact numbers, not suggestions, and you avoid the country’s sharpest financial edge.',
        keyPoints: [
          'No short-term vignette — annual sticker only',
          'Serious speeding fines scale with income',
          'Near-zero alcohol limit for novice/professional drivers',
        ],
      },
      {
        id: 'ch-m4-fines',
        title: 'The Hidden Fine Traps',
        minutes: 5,
        body: 'The trap in Switzerland is assuming a fine is small. Income-scaled penalties for serious speeding can reach figures far beyond a flat ticket, and the calculation is not obvious from the roadside. Camera fines may be forwarded weeks later through your rental company, which can add an admin fee. Do not ignore a notice hoping it disappears — unpaid Swiss penalties can escalate and follow you. Check any official notice carefully against your rental dates, pay valid fines promptly, and if something is wrong — wrong date, not the driver — contest it with your evidence rather than simply paying to make it go away.',
        keyPoints: [
          'Serious speeding fines can be very large (income-scaled)',
          'Fines may arrive later via the rental company',
          'Pay valid fines promptly; dispute genuine errors',
        ],
        check: {
          q: 'Switzerland offers a cheap daily or weekly motorway vignette for short trips. True or false?',
          options: [
            {
              label: 'False — only an annual vignette is sold',
              correct: true,
            },
            {
              label: 'True — buy a day pass at the border',
              correct: false,
            },
          ],
        },
      },
      {
        id: 'ch-m5-wrong',
        title: 'When Things Go Wrong',
        minutes: 5,
        body: 'In an emergency dial 112, which reaches all services. After a collision, move to safety, switch on hazards, and place the warning triangle before stepping out — put on a reflective vest first. Exchange details using the European Accident Statement, a standard bilingual form, instead of relying on memory or language you do not share. Photograph positions, plates and damage. In the mountains, be mindful of where you stop: never block a tunnel or a blind bend, and pull fully off the carriageway if you can. For injuries, theft or any dispute, wait for the authorities and keep your documents to hand.',
        keyPoints: [
          'Dial 112 for all emergencies',
          'Hi-vis vest + triangle before you exit',
          'Use the European Accident Statement; mind where you stop in tunnels',
        ],
      },
      {
        id: 'ch-m6-applied',
        title: 'Your Trip Applied',
        minutes: 5,
        body: 'Bring it together for a Swiss visit. Before leaving home: confirm the vignette, carry your IDP, and prepare for winter conditions if relevant. On the ground: keep right, treat limits as exact, give way uphill and to postal buses, and use low gears on descents. Day to day: never join a motorway without the vignette displayed, and stay well under the alcohol limit. After you return: watch for any forwarded fine notice, pay valid penalties promptly, and dispute genuine errors with your evidence. Plan the few Swiss-specific items and the rest of the drive is some of the most enjoyable in Europe.',
        keyPoints: [
          'Pre-trip: vignette, IDP, winter readiness',
          'On-trip: keep right, exact limits, mountain courtesy',
          'Post-trip: handle any forwarded fine promptly',
        ],
      },
    ],
  },
  emergency: {
    number: '112',
    steps: [
      {
        title: 'After an accident',
        detail: 'Stop, move to safety, switch on hazards and check everyone. Call 112 for injuries; give your location and number of people hurt.',
      },
      {
        title: 'European Accident Statement',
        detail: 'Complete the European Accident Statement with the other driver — it is standardised and bilingual, so use it instead of free-form notes.',
      },
      {
        title: 'Hazard signal + triangle',
        detail: 'Put on your reflective vest before leaving the car and place the warning triangle well back up the road; never block a tunnel or blind bend.',
      },
    ],
    phrases: [
      {
        en: 'I need an ambulance',
        local: 'Ich brauche einen Krankenwagen',
      },
      {
        en: 'I don\'t understand',
        local: 'Ich verstehe nicht',
      },
      {
        en: 'Where is the nearest hospital?',
        local: 'Wo ist das nächste Krankenhaus?',
      },
    ],
  },
}
